// src/components/FirebaseUIAuth.jsx
import { useEffect, useRef, useState } from 'react';
import { EmailAuthProvider, GoogleAuthProvider, FacebookAuthProvider, PhoneAuthProvider, UserCredential } from 'firebase/auth';
import * as firebaseui from 'firebaseui'; // Import the firebaseui library
import 'firebaseui/dist/firebaseui.css'; // Import the default CSS
import { auth } from '../lib/firebase'; // Your initialized Firebase auth instance
import { useAppDispatch } from '@/store/hooks';
import { checkAuthState } from '@/store/slices/authSlice';

// Define the FirebaseUI config
const uiConfig = {
  // --- Callbacks ---
  //signInSuccessUrl: '/profile', // Where to redirect after sign-in success (if not using callback)
  callbacks: {
    // Called when sign-in is successful. Returning false prevents redirect.
    signInSuccessWithAuthResult: function(authResult: any) {
      console.log('Sign in successful!', authResult);
      // You can handle the successful sign-in here (e.g., close modal, redirect)
      // Return false to prevent the default redirect behavior if you handle it yourself.
      return false;
    },
    // Called when the UI instance is displayed.
    uiShown: function() {
      // The widget is rendered. Hide the loader. (If you have one)
      console.log('FirebaseUI shown');
    }
  },

  // --- Options ---
  signInFlow: 'popup', // Use 'popup' for sign-in flow instead of redirect
  signInOptions: [
    // List of providers you enabled in the Firebase console
    GoogleAuthProvider.PROVIDER_ID,
    FacebookAuthProvider.PROVIDER_ID,
    EmailAuthProvider.PROVIDER_ID,
    {
         provider: PhoneAuthProvider.PROVIDER_ID,
         recaptchaParameters: {
             type: 'image', // 'image' or 'audio'
             size: 'invisible', // 'normal' or 'invisible' or 'compact'
             badge: 'bottomleft' //' bottomright' or 'inline' applies to invisible.
         },
         //defaultCountry: 'SE', // Optional: Set default country (e.g., 'US', 'GB')
         // You can provide custom whitelisted/blacklisted countries if needed
    }
    // Add other providers like TwitterAuthProvider.PROVIDER_ID, GithubAuthProvider.PROVIDER_ID, etc.
  ],
  // Optional: Terms of service url/callback. Required for Phone Auth & Email Enumeration protection.
  tosUrl: '/terms-of-service', // Replace with your actual TOS URL
  // Optional: Privacy policy url/callback. Required for Phone Auth & Email Enumeration protection.
  privacyPolicyUrl: '/privacy-policy', // Replace with your actual Privacy Policy URL
};

// Define Props interface
interface FirebaseUIAuthProps {
  onSuccess: (authResult: UserCredential) => void; // Type the onSuccess prop
}

function FirebaseUIAuth({ onSuccess }: FirebaseUIAuthProps) { // Apply the props interface
  const dispatch = useAppDispatch();
  const [uiInstance, setUiInstance] = useState<firebaseui.auth.AuthUI | null>(null); // Add type for uiInstance state
  const elementRef = useRef<HTMLDivElement>(null); // Type the ref element

  useEffect(() => {
    // Ensure auth is initialized and we don't have an existing instance
    if (auth && !uiInstance) {
      // Get or Create a FirebaseUI instance. Start happens below.
       const ui = firebaseui.auth.AuthUI.getInstance() || new firebaseui.auth.AuthUI(auth);
       setUiInstance(ui);
    }
    // Cleanup function
    return () => {
        // Instance cleanup is tricky with React StrictMode double render.
        // A simple approach might be to check if it exists and needs deleting
        // if(uiInstance) {
        //    uiInstance.delete().catch(err => console.error("Error deleting FirebaseUI:", err));
        // }
        // setUiInstance(null); // Be careful with state updates in cleanup
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  useEffect(() => {
      // Start the UI when the instance and element are ready
      if (uiInstance && elementRef.current) {
          // Pass the config object and the ID of the element to render into.
          uiInstance.start(elementRef.current, {
              ...uiConfig,
              callbacks: {
                  ...uiConfig.callbacks,
                  signInSuccessWithAuthResult: (authResult: UserCredential): boolean => { // Add types here too
                       // Call the original callback if it exists
                      uiConfig.callbacks?.signInSuccessWithAuthResult?.(authResult);
                      
                      // Explicitly dispatch checkAuthState to update Redux store
                      console.log('[FirebaseUIAuth] Dispatching checkAuthState after login');
                      dispatch(checkAuthState())
                        .then(result => {
                          console.log('[FirebaseUIAuth] checkAuthState completed:', result);
                        })
                        .catch(error => {
                          console.error('[FirebaseUIAuth] checkAuthState error:', error);
                        });
                      
                      // Call the onSuccess prop passed from parent (e.g., to close modal)
                      if (onSuccess) {
                          console.log('[FirebaseUIAuth] Calling parent onSuccess handler');
                          onSuccess(authResult);
                      }
                      return false; // Prevent redirect always if using callback
                  }
              }
          });
      }
  }, [uiInstance, onSuccess, dispatch]); // Re-run if uiInstance or onSuccess changes

  // Render the container element for FirebaseUI
  return <div ref={elementRef} />;
}

export default FirebaseUIAuth;